import React, { createContext, useContext, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { encryptData, decryptData } from '../lib/crypto'
import { googleDrive } from '../lib/googleDrive'
import { useAuth } from './AuthContext'
import toast from 'react-hot-toast'

const VaultContext = createContext({})

export const useVault = () => {
  const context = useContext(VaultContext)
  if (!context) {
    throw new Error('useVault must be used within a VaultProvider')
  }
  return context
}

export const VaultProvider = ({ children }) => {
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [currentPath, setCurrentPath] = useState('')
  const [folderView, setFolderView] = useState(null)
  const [folderPasswords, setFolderPasswords] = useState({}) // Cache folder passwords
  const { user } = useAuth()

  // Helper function to get user-specific path
  const getUserPath = (path = '') => {
    if (!user) return path
    const userPrefix = `users/${user.id}`
    return path ? `${userPrefix}/${path}` : userPrefix
  }

  // List files in the current directory
  const listFiles = useCallback(async (path = '') => {
    if (!user) {
      return []
    }

    try {
      setLoading(true)
      
      // Get files from Google Drive
      const driveFiles = await googleDrive.listFiles(user.id)
      
      // Separate folders and files
      const folders = driveFiles.filter(file => file.name.endsWith('.manifest'))
      const allFiles = driveFiles.filter(file => !file.name.endsWith('.manifest'))
      
      // Get folder names (without .manifest extension)
      const folderNames = folders.map(folder => folder.name.replace('.manifest', ''))
      
      // Filter out files that belong to folders (files with folder prefix in their path)
      const rootFiles = allFiles.filter(file => {
        // Check if this file belongs to any folder
        return !folderNames.some(folderName => 
          file.name.startsWith(folderName + '/') || 
          file.name.includes('/' + folderName + '/') ||
          file.name.startsWith(folderName + '\\') || 
          file.name.includes('\\' + folderName + '\\')
        )
      })
      
      // Process folders for display
      const processedFolders = folders.map(file => ({
        id: file.id,
        name: file.name,
        size: file.size,
        created_at: file.createdTime,
        updated_at: file.modifiedTime,
        isFolder: true,
        displayName: file.name.replace('.manifest', ''),
        path: file.id,
        mimeType: file.mimeType,
      }))
      
      // Process root files for display
      const processedRootFiles = rootFiles.map(file => ({
        id: file.id,
        name: file.name,
        size: file.size,
        created_at: file.createdTime,
        updated_at: file.modifiedTime,
        isFolder: false,
        displayName: file.name,
        path: file.id,
        mimeType: file.mimeType,
      }))

      // Combine folders and root files
      const processedFiles = [...processedFolders, ...processedRootFiles]

      setFiles(processedFiles)
      setCurrentPath(path)
      return processedFiles
    } catch (error) {
      toast.error(`Failed to load files: ${error.message}`)
      return []
    } finally {
      setLoading(false)
    }
  }, [user])

  // Upload a single file
  const uploadFile = async (file, password, path = '') => {
    try {
      const fileBuffer = await file.arrayBuffer()
      const encryptedBlob = await encryptData(fileBuffer, password)
      
      // Create a new File object from the encrypted blob
      const encryptedFile = new File([encryptedBlob], file.name, {
        type: 'application/octet-stream'
      })
      
      // Upload to Google Drive
      const result = await googleDrive.uploadFile(encryptedFile, file.name, user.id)
      
      return result
    } catch (error) {
      throw new Error(`Failed to upload ${file.name}: ${error.message}`)
    }
  }

  // Upload multiple files (folder)
  const uploadFolder = async (files, password, folderName) => {
    try {
      setUploading(true)
      
      // Create manifest for the folder
      const manifest = {
        files: Array.from(files).map(file => ({
          name: file.webkitRelativePath.replace(`${folderName}/`, ''),
          type: file.type,
          size: file.size,
        }))
      }

      // Upload all files to Google Drive
      const uploadPromises = Array.from(files).map(async (file, index) => {
        toast.loading(`Encrypting file ${index + 1}/${files.length}`, { id: 'upload-progress' })
        
        const fileBuffer = await file.arrayBuffer()
        const encryptedBlob = await encryptData(fileBuffer, password)
        
        // Create encrypted file for Google Drive
        const encryptedFile = new File([encryptedBlob], file.webkitRelativePath, {
          type: 'application/octet-stream'
        })
        
        // Upload to Google Drive
        await googleDrive.uploadFile(encryptedFile, file.webkitRelativePath, user.id)
      })

      await Promise.all(uploadPromises)

      // Upload manifest to Google Drive
      toast.loading('Finalizing folder...', { id: 'upload-progress' })
      const manifestBuffer = new TextEncoder().encode(JSON.stringify(manifest))
      const encryptedManifest = await encryptData(manifestBuffer, password)
      
      // Create manifest file for Google Drive
      const manifestFile = new File([encryptedManifest], `${folderName}.manifest`, {
        type: 'application/octet-stream'
      })
      
      await googleDrive.uploadFile(manifestFile, `${folderName}.manifest`, user.id)

      toast.success('Folder uploaded successfully!', { id: 'upload-progress' })
      
      // Refresh file list
      await listFiles(currentPath)
      
    } catch (error) {
      toast.error(error.message, { id: 'upload-progress' })
      throw error
    } finally {
      setUploading(false)
    }
  }

  // Download and decrypt a file
  const downloadFile = async (fileId, password, fileName = null) => {
    try {
      // If no password provided, try to use cached folder password
      let actualPassword = password
      
      if (!actualPassword && folderView && folderView.password) {
        // Use the folder password if we're inside a folder
        actualPassword = folderView.password
      }
      
      if (!actualPassword) {
        throw new Error('No password provided and no cached password available')
      }
      
      // Download from Google Drive using file ID
      const arrayBuffer = await googleDrive.downloadFile(fileId)
      const decryptedData = await decryptData(arrayBuffer, actualPassword)
      return decryptedData
    } catch (error) {
      console.error('Download error:', error)
      throw new Error(`Failed to download file: ${error.message}`)
    }
  }

  // Open a folder (decrypt manifest and show contents)
  const openFolder = async (folderName, password) => {
    try {
      setLoading(true)
      
      // Find the manifest file in Google Drive
      const driveFiles = await googleDrive.listFiles(user.id)
      const manifestFile = driveFiles.find(file => file.name === `${folderName}.manifest`)
      
      if (!manifestFile) {
        throw new Error('Folder manifest not found')
      }

      // Download and decrypt manifest
      const arrayBuffer = await googleDrive.downloadFile(manifestFile.id)
      const decryptedManifest = await decryptData(arrayBuffer, password)
      const manifest = JSON.parse(new TextDecoder().decode(decryptedManifest))
      
      // Cache the folder password
      setFolderPasswords(prev => ({
        ...prev,
        [folderName]: password
      }))
      
      setFolderView({
        name: folderName,
        password,
        manifest,
        path: folderName
      })
      
      // Get files that belong to this folder from Google Drive
      const folderFiles = driveFiles.filter(file => 
        file.name.startsWith(`${folderName}/`) || 
        file.name.startsWith(`${folderName}\\`)
      )
      
      // Map manifest files to actual Google Drive files
      const processedFiles = manifest.files.map(manifestFile => {
        const driveFile = folderFiles.find(df => 
          df.name.endsWith(manifestFile.name) || 
          df.name === `${folderName}/${manifestFile.name}` ||
          df.name === `${folderName}\\${manifestFile.name}`
        )
        
        return {
          ...manifestFile,
          id: driveFile?.id || null,
          isFolder: false,
          displayName: manifestFile.name,
          path: driveFile?.id || `${folderName}/${manifestFile.name}`,
          belongsToFolder: folderName, // Mark that this file belongs to a folder
        }
      })
      
      setFiles(processedFiles)
      setCurrentPath(folderName)
      
      return manifest
    } catch (error) {
      toast.error(`Failed to open folder: ${error.message}`)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Go back to parent directory
  const goBack = () => {
    if (folderView) {
      // Clear the cached password for this folder when leaving
      setFolderPasswords(prev => {
        const newPasswords = { ...prev }
        delete newPasswords[folderView.name]
        return newPasswords
      })
      setFolderView(null)
      listFiles('')
    }
  }

  // Delete a file
  const deleteFile = async (fileId) => {
    try {
      // Delete from Google Drive using file ID
      await googleDrive.deleteFile(fileId)

      toast.success('File deleted successfully')
      await listFiles(currentPath)
    } catch (error) {
      toast.error(`Failed to delete file: ${error.message}`)
      throw error
    }
  }

  const value = {
    files,
    loading,
    uploading,
    currentPath,
    folderView,
    folderPasswords,
    listFiles,
    uploadFile,
    uploadFolder,
    downloadFile,
    openFolder,
    goBack,
    deleteFile,
  }

  return (
    <VaultContext.Provider value={value}>
      {children}
    </VaultContext.Provider>
  )
}
